import { Component, inject, signal, ChangeDetectionStrategy, computed, NgZone, DestroyRef } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { CommonModule } from '@angular/common';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';
import { RemoteConfigService } from '../../shared/services/remote-config.service';
import { hasRole } from '../../shared/models/interfaces/user.model';
import { environment } from '../../../environments/environment';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { UserMenu } from './user-menu';

@Component({
  selector: 'app-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive, CommonModule, MatToolbarModule, MatButtonModule, MatIconModule, MatDividerModule, UserMenu],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header {
  public authService = inject(AuthService);
  private remoteConfig = inject(RemoteConfigService);
  private ngZone = inject(NgZone);
  private destroyRef = inject(DestroyRef);

  // ── Compile-time feature flags ─────────────────────────────────
  private readonly envHideAIItems = environment.hideAIItems;
  private readonly envHideAdminItems = environment.hideAdminItems;
  private readonly envDisableChatBubble = environment.disableChatBubble;

  // ── UI state ────────────────────────────────────────────────────
  isScrolled = signal(false);
  isMenuOpen = signal(false);

  // ── Auth-derived signals ────────────────────────────────────────
  readonly isAuthenticated = toSignal(this.authService.authStatus$, { initialValue: false });
  readonly currentUser = toSignal(this.authService.user$, { initialValue: null });

  readonly userDisplayName = computed(() => {
    const user = this.currentUser();
    return user?.firstName || user?.displayName || user?.username || 'User';
  });

  readonly userEmail = computed(() => this.currentUser()?.email || '');

  readonly accountRoleLabel = computed(() => {
    const user = this.currentUser();
    if (!user?.roles) return 'Customer';
    if (hasRole(user.roles, 'superadmin')) return 'Super admin';
    if (hasRole(user.roles, 'admin')) return 'Admin';
    if (hasRole(user.roles, 'service_provider')) return 'Provider';
    return 'Customer';
  });

  readonly userImage = computed(() => {
    const user = this.currentUser();
    return this.resolveImage(user?.images);
  });

  /** Normalize user images: handles string, array, or object with url property */
  private resolveImage(images: unknown): string | null {
    if (!images) return null;

    if (typeof images === 'string' && images.trim()) {
      return images;
    }

    if (Array.isArray(images) && images.length > 0) {
      const first = images[0];
      if (!first) return null;
      if (typeof first === 'object' && first !== null) {
        const obj = first as Record<string, unknown>;
        return (typeof obj['url'] === 'string' && obj['url'].trim())
          ? obj['url']
          : (typeof obj['path'] === 'string' && obj['path'].trim())
            ? obj['path']
            : null;
      }
      return typeof first === 'string' && first.trim() ? first : null;
    }

    return null;
  }

  // ── Pure role checks (unchanged by env flags) ───────────────────
  readonly isAdminOrProvider = computed(() => {
    const user = this.currentUser();
    if (!user?.roles) return false;
    return hasRole(user.roles, 'admin') || hasRole(user.roles, 'service_provider') || hasRole(user.roles, 'superadmin');
  });

  readonly isAdmin = computed(() => {
    const user = this.currentUser();
    if (!user?.roles) return false;
    return hasRole(user.roles, 'admin') || hasRole(user.roles, 'superadmin');
  });

  // ── Remote-config AI flag ───────────────────────────────────────
  readonly isAIEnabled = toSignal(this.remoteConfig.config$.pipe(
    map(c => c?.features?.ai?.enabled ?? false)
  ), { initialValue: false });

  // ── Env-gated combined signals (AND-gate: env + role + remote) ─
  /** Admin/provider-level items (Dashboard, Services, Jobs, Earnings) */
  readonly showAdminItems = computed(() =>
    !this.envHideAdminItems && this.isAdminOrProvider()
  );

  /** Provider-only items (Status dashboard) */
  readonly showProviderMenu = computed(() => {
    const user = this.currentUser();
    return !!user?.roles && hasRole(user.roles, 'service_provider');
  });

  /** Admin-only items (Users, Providers, Payouts, Paystack) */
  readonly showAdminRestricted = computed(() =>
    !this.envHideAdminItems && this.isAdmin()
  );

  /** AI Models link — requires env AI not hidden, env admin not hidden, admin role, and remote AI enabled */
  readonly showAIModels = computed(() =>
    !this.envHideAIItems && !this.envHideAdminItems && this.isAdmin() && this.isAIEnabled()
  );

  /** AI Agents link — same gates as AI Models */
  readonly showAIAgents = computed(() =>
    !this.envHideAIItems && !this.envHideAdminItems && this.isAdmin() && this.isAIEnabled()
  );

  /** Chat bubble visibility — env flag AND remote config AI enabled */
  readonly showChatBubble = computed(() =>
    !this.envDisableChatBubble && this.isAIEnabled()
  );

  // ── Image error tracking ────────────────────────────────────────
  readonly imageErrored = signal(false);

  clearImage() {
    this.imageErrored.set(true);
  }

  // ── Zone-optimized scroll listener (avoids CD on every pixel) ──
  constructor() {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'scroll', { passive: true })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          const shouldBeScrolled = window.scrollY > 20;
          if (this.isScrolled() !== shouldBeScrolled) {
            this.ngZone.run(() => this.isScrolled.set(shouldBeScrolled));
          }
        });
    });
  }

  // ── Menu toggle / close with body scroll lock ──────────────────
  toggleMenu() {
    this.isMenuOpen.update(v => {
      const next = !v;
      this.setBodyScrollLock(next);
      return next;
    });
  }

  closeMenu() {
    this.isMenuOpen.set(false);
    this.setBodyScrollLock(false);
  }

  /** Lock body scroll when the mobile drawer is open */
  private setBodyScrollLock(lock: boolean) {
    if (lock) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = parseInt(document.body.style.top || '0', 10) * -1;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    }
  }

  // ── Logout ──────────────────────────────────────────────────────
  logout() {
    this.authService.logout();
    this.closeMenu();
  }
}
